'use client';

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import clsx from 'clsx';
import { FeedType, ProductType, RecommendationType } from '@/@types';
import { Button, Icon, Modal, Spinner } from '@/components/core';
import { UIComponent } from '@/components/core/components';
import { IconType } from '@/components/core/components/atoms/icon/icons';
import { useMounted, useOnViewport } from '@/components/core/hooks';
import { useLingui } from '@lingui/react';
import { categories } from '@/@constants';
import * as styles from './feed-card.styles';
import FeedBackground from '../feed-background';

const options = { threshold: 0.75 };

const getPreviewIcon = (product: FeedType['product'], playing = false) => {
  const { preview } = product;

  if (!preview) {
    return undefined;
  }

  if (preview.type === 'youtube') {
    return (playing ? 'close' : 'play') as IconType;
  }
  if (preview.type === 'music') {
    return (playing ? 'mute' : 'play') as IconType;
  }
  return undefined;
};

const FeedCard: UIComponent<{
  product: ProductType;
  recommendation?: RecommendationType;
  withoutOverlay?: boolean;
  onStop?: CallableFunction;
  playing?: boolean;
  onViewport?: CallableFunction;
}> = ({ product, children, onPlay, onStop, withoutOverlay, playing, onViewport: onViewportProp, recommendation, ...props }) => {
  const playerId = useId();
  const player = useRef<any>();
  const videoPlayerRef = useRef<HTMLVideoElement>(null);
  const ref = useRef(null);
  const { i18n } = useLingui();
  const [muted, setMuted] = useState(true);
  const mounted = useMounted();
  const category = categories[product.category.name];

  const image =
    recommendation?.media?.find((media) => {
      return media.type === 'image';
    }) ||
    product.media.find((media) => {
      return media.type === 'image';
    });

  const video =
    recommendation?.media?.find((media) => {
      return media.type === 'video';
    }) ||
    product.media.find((media) => {
      return media.type === 'video';
    });

  const squareMiniature = ['music'].includes(product.category.name) && !video;
  const [loading, setIsLoading] = useState(!!video);

  useEffect(() => {
    const videoElement = videoPlayerRef.current;
    if (!videoElement) {
      return;
    }
    videoElement.muted = muted;
  }, [muted]);

  useEffect(() => {
    if (video) {
      const videoElement = videoPlayerRef.current!;
      player.current = {
        play: () => {
          videoElement.muted = !navigator.userActivation.hasBeenActive;
          setMuted(!navigator.userActivation.hasBeenActive);
          videoElement.play().catch(() => {});
        },
        stop: () => {
          videoElement.pause();
        },
        _player: videoElement,
      };

      return () => {
        videoElement.pause();
        player.current = null;
      };
    }
    if (!product.preview) {
      return undefined;
    }
    if (product.preview.type === 'youtube') {
      let _player: any;

      player.current = {
        play: () => {
          import('youtube-player').then(({ default: YouTubePlayer }) => {
            _player =
              _player ||
              YouTubePlayer(playerId, {
                videoId: product.preview?.id,
                height: 'auto',
                width: 'auto',
              });
            _player.playVideo();
          });
        },
        stop: () => {
          _player?.stopVideo();
        },
        _player,
      };
      return () => {
        if (!_player) {
          return;
        }
        _player.destroy();
        _player = null;
      };
    }
    if (product.preview.type === 'music') {
      let _player: any;
      player.current = {
        play: () => {
          _player = _player || new Audio(product.preview?.url);
          _player.play();
        },
        stop: () => {
          _player?.pause();
          _player = new Audio(product?.preview?.url);
        },
        _player,
      };
      return () => {
        if (!_player) {
          return;
        }
        _player?.pause();
        _player = null;
      };
    }
    return undefined;
  }, [product, video, playerId]);

  useEffect(() => {
    if (!player.current || playing === undefined) {
      return;
    }
    if (playing) {
      player.current.play();
    } else {
      player.current.stop();
    }
  }, [playing]);

  const togglePlayPreview = (evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    if (!playing) {
      onPlay?.();
    } else {
      onStop?.();
    }
  };

  const toggleSound: React.MouseEventHandler<HTMLButtonElement> = (evt) => {
    setMuted(!muted);
    evt.stopPropagation();
    evt.nativeEvent.stopImmediatePropagation();
  };

  const onViewport = useCallback(
    (isOnViewport) => {
      onViewportProp?.(isOnViewport);
      if (!isOnViewport) {
        onStop?.();
      }
    },
    [onStop, onViewportProp],
  );

  useOnViewport(ref, onViewport, { options });

  return (
    <FeedBackground
      alt={`${i18n._(category.itemTitle)} ${product.name}`}
      ref={ref}
      image={video ? undefined : image?.source}
      blur={!!video}
      withoutOverlay={withoutOverlay}
      squareMiniature={squareMiniature}
      {...props}
    >
      {video && (
        <>
          <video
            {...(mounted && { src: video.source })}
            ref={videoPlayerRef}
            className={styles.video}
            muted
            loop
            playsInline
            autoPlay={false}
            onLoadedMetadata={() => {
              setIsLoading(false);
            }}
          />
          {playing && (
            <Button className={clsx(styles.play)} variant="tertiary" onClick={toggleSound}>
              <Icon name={(muted ? 'voice' : 'mute') as IconType} />
            </Button>
          )}
          {loading && <Spinner className={styles.spinner} />}
        </>
      )}
      {children}
      {product.preview && !video && playing !== undefined && (
        <Button className={clsx(styles.play, { [styles.playing]: playing })} variant="tertiary" onClick={togglePlayPreview}>
          <Icon name={getPreviewIcon(product, playing)} />
        </Button>
      )}
      {product.preview?.type === 'youtube' && !video && (
        <Modal open={!!playing} onClose={togglePlayPreview} mountContent>
          <div id={playerId} className={styles.player} />
        </Modal>
      )}
    </FeedBackground>
  );
};

export default FeedCard;
